import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Send, Share2, MessageCircle, Copy, Download, ImageIcon, Shield, Sparkles, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatPayout } from "@/lib/payout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { ProductImage } from "@/components/ProductImage";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [shareUrl, setShareUrl] = useState("");  // tracked bank apply URL with sub-id baked in
  const [form, setForm] = useState({ customer_name: "", customer_mobile: "", customer_email: "", city: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/products/${id}`);
        if (!cancelled) setP(r.data);
        // Pre-build the tracked bank apply URL so Share buttons share the actual product
        // (not the platform signup link). Falls back to platform deep-link if the bank URL is missing.
        try {
          const { data } = await api.get(`/clicks/build-url?product_id=${id}`);
          if (!cancelled) setShareUrl(data.url || "");
        } catch { /* if bank URL missing, fall back below */ void 0; }
      } catch (err) {
        if (!cancelled) toast.error(err.response?.data?.detail || "Failed to load product");
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  // If the product has no bank apply_url configured, share a deep-link to the
  // product page on Crece itself (still carries the consultant's referral code
  // via ?ref=). This keeps attribution intact regardless of bank setup.
  const platformProductLink = (() => {
    if (typeof window === "undefined") return "";
    const params = new URLSearchParams();
    if (user?.referral_code) params.set("ref", user.referral_code);
    return `${window.location.origin}/app/products/${id}${params.toString() ? `?${params.toString()}` : ""}`;
  })();

  // Owned products (DCA, future Crece products) skip the bank-apply tracking
  // URL entirely — agents share the self-serve wizard at /protect?ref=CODE
  // instead. The wizard captures device/plan/addons + creates the lead AND
  // the draft policy in one go, so we don't want WhatsApp shares pointing at
  // the agent-only product detail page.
  const ownedWizardUrl = (() => {
    if (typeof window === "undefined") return "";
    const params = new URLSearchParams();
    if (user?.referral_code) params.set("ref", user.referral_code);
    const path = p?.checkout_path || "/protect";
    return `${window.location.origin}${path}${params.toString() ? `?${params.toString()}` : ""}`;
  })();

  const finalShareUrl = p?.product_type === "owned"
    ? ownedWizardUrl
    : (shareUrl || platformProductLink);

  const posterUrl = p?.poster_meta
    ? `${BACKEND}/api/products/${id}/poster?v=${encodeURIComponent(p.poster_meta.uploaded_at || "")}`
    : null;

  const shareWhatsApp = async () => {
    try { await api.post("/share/track", { product_id: id, channel: "whatsapp" }); } catch { void 0; }
    const text = p.product_type === "owned"
      ? `Protect your device with *${p.name}* 🛡️\n\n${p.description || ""}\n\nGet an instant quote: ${finalShareUrl}\n\nOur team will reach out to confirm payment & activate.`
      : `Hi! Check out *${p.name}* from ${p.bank}.\n\n${p.description || ""}\n\nApply here: ${finalShareUrl}`;
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const downloadPoster = async () => {
    if (!posterUrl) return;
    try { await api.post("/share/track", { product_id: id, channel: "poster" }); } catch { void 0; }
    const filename = `${p.bank || "crece"}-${p.name}.${p.poster_meta?.ext || "jpg"}`.replace(/[^a-z0-9.-]+/gi, "-").toLowerCase();
    // Build the download URL — appending `download=1` so the backend sends a
    // `Content-Disposition: attachment` header. Without that header, browsers
    // ignore the <a download> attribute on cross-origin URLs (preview frontend
    // ≠ backend domain) and just open the image inline in a new tab.
    const sep = posterUrl.includes("?") ? "&" : "?";
    const dlUrl = `${posterUrl}${sep}download=1`;
    // Try fetch → blob → object-URL for an unmistakable browser-save UX even on
    // strict same-site contexts. Falls back to a plain anchor click on failure.
    try {
      const res = await fetch(dlUrl, { credentials: "omit" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const obj = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = obj;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(obj), 5000);
      toast.success("Poster downloaded — check your Downloads folder.");
    } catch (e) {
      // Fallback: direct link click (will at least open the file in a new tab
      // with the attachment header forcing browser save in most cases).
      const a = document.createElement("a");
      a.href = dlUrl;
      a.download = filename;
      a.target = "_blank";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  const sharePosterOnWhatsApp = async () => {
    if (!posterUrl) return;
    try { await api.post("/share/track", { product_id: id, channel: "poster_whatsapp" }); } catch { void 0; }
    const text = `*${p.name}* — ${p.bank}\n\nApply here: ${finalShareUrl}\n\nPoster: ${posterUrl}`;
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const copyLink = async () => {
    try { await api.post("/share/track", { product_id: id, channel: "copy" }); } catch { void 0; }
    navigator.clipboard.writeText(finalShareUrl);
    toast.success("Product link copied — paste anywhere!");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name || form.customer_mobile.length < 10) { toast.error("Customer name and 10-digit mobile required"); return; }
    setSubmitting(true);

    // IMPORTANT: open a blank window NOW, inside the user-gesture context.
    // If we wait until after `await api.post(...)` the browser will block window.open
    // as a popup. We'll redirect this tab to the final URL once we have it; if
    // anything fails we close it (or fall back to redirecting the current tab).
    let popup = null;
    try { popup = window.open("about:blank", "_blank"); } catch { popup = null; }

    try {
      await api.post("/leads", { product_id: id, ...form });

      // Build & open the apply URL
      let finalUrl = "";
      try {
        const { data } = await api.get(`/clicks/build-url?product_id=${id}`);
        finalUrl = (data && data.url) || "";
      } catch { /* fall back below */ void 0; }
      if (!finalUrl) finalUrl = (p.apply_url || "").trim();

      if (finalUrl) {
        try { await api.post("/clicks/log", { product_id: id, final_url: finalUrl }); } catch { void 0; }
        if (popup && !popup.closed) {
          popup.location.href = finalUrl;
        } else {
          // Popup was blocked or user closed it — redirect current tab
          window.location.href = finalUrl;
          return;
        }
        toast.success("Lead saved. Opening the bank's application page in a new tab.");
      } else {
        if (popup && !popup.closed) popup.close();
        toast.warning("Lead saved, but this product has no apply link configured yet. Ask admin to add the bank's apply URL.");
      }
      navigate("/app/leads");
    } catch (err) {
      if (popup && !popup.closed) popup.close();
      toast.error(err.response?.data?.detail || "Failed to submit lead");
    } finally {
      setSubmitting(false);
    }
  };

  if (!p) return <div className="text-stone-500">Loading...</div>;

  return (
    <div className="space-y-8" data-testid="product-detail-page">
      <Link to="/app/products" className="inline-flex items-center text-sm font-semibold text-stone-600 hover:text-emerald-900"><ArrowLeft className="w-4 h-4 mr-1" /> Back to products</Link>

      <div className="grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-5">
          <ProductImage product={p} className="w-full h-72 object-cover rounded-2xl" />
          <div className="mt-6">
            <div className="text-xs font-bold uppercase tracking-wider text-amber-700">{p.category}</div>
            <h1 className="font-display text-4xl font-black mt-2 tracking-tight">{p.name}</h1>
            <p className="text-stone-500 mt-1">{p.bank}</p>
            <p className="text-stone-700 mt-4 leading-relaxed">{p.description}</p>
            {!user?.corporate_id && <div className="mt-6 bg-emerald-900 text-stone-100 rounded-xl p-5">
              <div className="text-xs font-bold uppercase tracking-widest text-amber-400">Payout</div>
              <div className="font-display text-3xl font-black mt-2">{formatPayout(p)}</div>
            </div>}

            {Array.isArray(p.kpis) && p.kpis.filter((k) => k && k.trim()).length > 0 && (
              <div className="mt-6 bg-white border border-stone-200 rounded-xl p-5" data-testid="product-kpis">
                <div className="text-xs font-bold uppercase tracking-widest text-emerald-800">Key Highlights</div>
                <ul className="mt-3 space-y-2">
                  {p.kpis.filter((k) => k && k.trim()).map((k, idx) => (
                    <li key={idx} data-testid={`product-kpi-${idx}`} className="flex items-start gap-2 text-sm text-stone-700 leading-relaxed">
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
                      <span>{k}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {posterUrl && (
              <div className="mt-6 bg-gradient-to-br from-amber-50 to-amber-100 border border-amber-300 rounded-xl p-5" data-testid="product-poster-card">
                <div className="flex items-center gap-2 mb-3">
                  <ImageIcon className="w-4 h-4 text-amber-800" />
                  <div className="text-xs font-bold uppercase tracking-widest text-amber-900">Marketing Poster</div>
                </div>
                <a href={posterUrl} target="_blank" rel="noopener noreferrer" className="block group" data-testid="product-poster-thumb-link">
                  <img src={posterUrl} alt={`${p.name} poster`}
                    className="w-full max-h-[420px] object-contain rounded-lg bg-white border border-amber-200 group-hover:border-amber-400 transition" />
                </a>
                <p className="text-[11px] text-stone-600 mt-2">Tap the image to view full size, or use the buttons below to download / share with your prospect.</p>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <Button onClick={downloadPoster} variant="outline" className="rounded-full border-amber-400 bg-white hover:bg-amber-50 font-bold h-10 text-amber-900" data-testid="poster-download-btn">
                    <Download className="w-4 h-4 mr-1.5" /> Download
                  </Button>
                  <Button onClick={sharePosterOnWhatsApp} className="rounded-full bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold h-10" data-testid="poster-share-whatsapp-btn">
                    <MessageCircle className="w-4 h-4 mr-1.5" /> Share Poster
                  </Button>
                </div>
              </div>
            )}

            <div className="mt-5 grid grid-cols-2 gap-3">
              <Button data-testid="whatsapp-share-button" onClick={shareWhatsApp}
                className="rounded-full bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold h-12">
                <MessageCircle className="w-4 h-4 mr-2" /> Share on WhatsApp
              </Button>
              <Button data-testid="copy-share-link-button" onClick={copyLink} variant="outline"
                className="rounded-full border-stone-300 font-bold h-12">
                <Copy className="w-4 h-4 mr-2" /> Copy Link
              </Button>
            </div>
          </div>
        </div>
        <div className="lg:col-span-7">
          <div className="bg-white rounded-2xl border border-stone-200 p-7">
            {p.product_type === "owned" ? (
              // ── Owned product (DCA-like) — agents can't fill a generic lead
              // because we already have a dedicated self-serve wizard at
              // /protect?ref=AGENT_CODE that prices the policy live and
              // captures the right device/plan/addon data. Show a focused
              // CTA that opens the wizard pre-attributed to this agent (or
              // copies the share link for WhatsApp).
              <OwnedProductCTA product={p} user={user} />
            ) : (
              <>
                <div className="flex items-center gap-2 mb-1"><Send className="w-4 h-4 text-emerald-900" /><span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-800">Submit Lead</span></div>
                <h2 className="font-display text-2xl font-black tracking-tight">Customer Details</h2>
                <p className="text-sm text-stone-500 mt-1">We&apos;ll verify and call the customer. You earn on conversion.</p>
                {p.apply_url && (
                  <div className="mt-3 text-xs bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-emerald-900" data-testid="apply-url-hint">
                    💡 After you submit, the official application page will open in a new tab.
                  </div>
                )}
                <form onSubmit={submit} className="mt-6 space-y-4">
                  <div>
                    <Label className="font-semibold">Customer Name *</Label>
                    <Input data-testid="lead-customer-name" required value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="mt-1.5 h-11 rounded-lg border-stone-300" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="font-semibold">Mobile *</Label>
                      <Input data-testid="lead-customer-mobile" required maxLength={10} value={form.customer_mobile} onChange={(e) => setForm({ ...form, customer_mobile: e.target.value.replace(/\D/g, "") })} className="mt-1.5 h-11 rounded-lg border-stone-300" />
                    </div>
                    <div>
                      <Label className="font-semibold">Email</Label>
                      <Input data-testid="lead-customer-email" type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} className="mt-1.5 h-11 rounded-lg border-stone-300" />
                    </div>
                  </div>
                  <div>
                    <Label className="font-semibold">City</Label>
                    <Input data-testid="lead-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1.5 h-11 rounded-lg border-stone-300" />
                  </div>
                  <div>
                    <Label className="font-semibold">Notes</Label>
                    <Textarea data-testid="lead-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Any relevant context..." className="mt-1.5 rounded-lg border-stone-300" />
                  </div>
                  <Button type="submit" data-testid="submit-lead-button" disabled={submitting}
                    className="w-full h-12 rounded-full bg-emerald-900 hover:bg-emerald-950 text-white font-bold">
                    {submitting ? "Submitting..." : "Submit Lead"} <Send className="ml-2 w-4 h-4" />
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// CTA shown on /products/<owned-product-id>. Replaces the generic lead form
// with three actions: (1) open the public wizard in a new tab to walk a
// prospect through over a call/screen-share, (2) copy the share link, and
// (3) WhatsApp the pre-built share text.
function OwnedProductCTA({ product, user }) {
  const code = user?.referral_code || "";
  const path = product?.checkout_path || "/protect";
  const wizardUrl = code
    ? `${window.location.origin}${path}?ref=${code}`
    : `${window.location.origin}${path}`;

  const waText = encodeURIComponent(
    `Protect your phone with ${product.name} 🛡️\n\n` +
    `• Covers screen, battery, motherboard & accidental damage\n` +
    `• Pickup & drop repair at 1000+ centres pan-India\n` +
    `• Up to 3 claims per year — pay nothing extra at the time of repair\n\n` +
    `Get an instant quote → ${wizardUrl}\n\n` +
    `Our team will reach out once you submit. Happy to answer any questions!`
  );
  const waUrl = `https://wa.me/?text=${waText}`;

  const copyLink = () => {
    navigator.clipboard.writeText(wizardUrl);
    toast.success("Wizard link copied!");
  };

  return (
    <div data-testid="owned-product-cta">
      <div className="flex items-center gap-2 mb-1">
        <Shield className="w-4 h-4 text-emerald-900" />
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-800">Owned product · self-serve</span>
      </div>
      <h2 className="font-display text-2xl font-black tracking-tight">Sell with the live quote wizard</h2>
      <p className="text-sm text-stone-500 mt-1">
        This is a Crece-owned product. Customers go through a guided wizard
        that prices their plan live based on device & age, then our ops team
        contacts them to collect payment.
      </p>

      {/* Why this is different from the standard lead form */}
      <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50/60 p-3 text-sm text-emerald-900">
        <div className="flex items-start gap-2">
          <Sparkles className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            Don&apos;t fill a generic lead — the wizard captures the device, plan, and add-ons
            (so we issue the right policy) and shows the customer their exact premium upfront.
          </div>
        </div>
      </div>

      <div className="mt-6 grid gap-3">
        <a href={wizardUrl} target="_blank" rel="noreferrer" className="block" data-testid="owned-open-wizard">
          <Button className="w-full h-12 rounded-full bg-emerald-900 hover:bg-emerald-950 text-white font-bold">
            Open the quote wizard <ExternalLink className="ml-2 w-4 h-4" />
          </Button>
        </a>
        <div className="grid grid-cols-2 gap-3">
          <Button onClick={copyLink} variant="outline" className="h-11 rounded-full border-stone-300 font-bold" data-testid="owned-copy-link">
            <Copy className="w-4 h-4 mr-2" /> Copy link
          </Button>
          <a href={waUrl} target="_blank" rel="noreferrer" className="block">
            <Button className="w-full h-11 rounded-full bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold" data-testid="owned-whatsapp">
              <MessageCircle className="w-4 h-4 mr-2" /> Share on WhatsApp
            </Button>
          </a>
        </div>
      </div>

      <div className="mt-6 pt-4 border-t border-stone-200">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-stone-500 mb-2">Your share link</div>
        <code className="text-xs font-mono text-stone-700 break-all" data-testid="owned-wizard-url">{wizardUrl}</code>
      </div>
    </div>
  );
}
